import { useEffect, useState } from "react";
import { z } from "zod";
import { MessageSquare, Send, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";

interface ForumComment {
  id: string;
  name: string;
  message: string;
  createdAt: number;
  categoryId: string;
}

const commentSchema = z.object({
  name: z
    .string()
    .trim()
    .nonempty({ message: "Please share your name" })
    .max(60, { message: "Name must be under 60 characters" }),
  message: z
    .string()
    .trim()
    .nonempty({ message: "Please write a comment" })
    .max(800, { message: "Comment must be under 800 characters" }),
});

const STORAGE_KEY = "duri_faq_forum_comments";

interface FAQForumProps {
  categoryId: string;
  categoryTitle: string;
}

const FAQForum = ({ categoryId, categoryTitle }: FAQForumProps) => {
  const [comments, setComments] = useState<ForumComment[]>([]);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setComments(JSON.parse(raw));
    } catch {
      // ignore
    }
  }, []);

  const persist = (next: ForumComment[]) => {
    setComments(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore quota
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const result = commentSchema.safeParse({ name, message });
    if (!result.success) {
      toast({
        title: "Could not post comment",
        description: result.error.issues[0]?.message ?? "Invalid input",
        variant: "destructive",
      });
      return;
    }
    const newComment: ForumComment = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: result.data.name,
      message: result.data.message,
      createdAt: Date.now(),
      categoryId,
    };
    persist([newComment, ...comments]);
    setName("");
    setMessage("");
    toast({ title: "Comment posted", description: "Thanks for joining the conversation!" });
  };

  const categoryComments = comments.filter((c) => c.categoryId === categoryId);

  return (
    <section
      aria-labelledby="forum-heading"
      className="mt-12 rounded-xl border border-border bg-card p-6 shadow-sm"
    >
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
          <MessageSquare className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h2 id="forum-heading" className="text-xl font-bold text-foreground">
            Community Discussion
          </h2>
          <p className="text-sm text-muted-foreground">
            Ask, share, and help fellow riders about {categoryTitle.toLowerCase()}.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <Input
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={60}
          aria-label="Your name"
        />
        <Textarea
          placeholder="Share your question, tip, or experience..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={800}
          rows={3}
          aria-label="Your comment"
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{message.length}/800</span>
          <Button type="submit" className="gap-2">
            <Send className="h-4 w-4" /> Post Comment
          </Button>
        </div>
      </form>

      <div className="mt-8 space-y-4">
        {categoryComments.length === 0 ? (
          <p className="rounded-lg bg-muted/40 p-4 text-center text-sm text-muted-foreground">
            Be the first to start the conversation in this category.
          </p>
        ) : (
          categoryComments.map((c) => (
            <article
              key={c.id}
              className="rounded-lg border border-border bg-background p-4"
            >
              <header className="mb-2 flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                  <User className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{c.name}</p>
                  <time className="text-xs text-muted-foreground">
                    {new Date(c.createdAt).toLocaleString()}
                  </time>
                </div>
              </header>
              <p className="whitespace-pre-wrap text-sm text-foreground/90">{c.message}</p>
            </article>
          ))
        )}
      </div>
    </section>
  );
};

export default FAQForum;
